<script setup>
import { computed, ref, watch } from 'vue'
import { router, usePage, usePoll } from '@inertiajs/vue3'
import EditAssetModal from '../../components/EditAssetModal.vue'
import VideoPlayerModal from '../../components/VideoPlayerModal.vue'

const props = defineProps({
  assets: { type: Array, default: () => [] },
  // snake_case to match the controller props (cf. thumb_url, byte_size) —
  // Inertia does not camelCase prop keys.
  q: { type: String, default: '' },
  page: { type: Number, default: 1 },
  per_page: { type: Number, default: 24 },
  total: { type: Number, default: 0 },
})

// Tenant is the active session tenant (sidebar switcher), shared app-wide.
// The edit modal still needs the full list to assign membership.
const tenants = computed(() => usePage().props.tenants ?? [])

// Edit/delete happen in a single modal reused across cards.
const editingAsset = ref(null)
const editOpen = ref(false)
const openEdit = (asset) => {
  editingAsset.value = asset
  editOpen.value = true
}

// Video playback happens in a single modal reused across cards.
const playingAsset = ref(null)
const playerOpen = ref(false)
const openPlayer = (asset) => {
  playingAsset.value = asset
  playerOpen.value = true
}

const formatBytes = (bytes) =>
  bytes == null ? '…' : bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

const formatDuration = (seconds) => {
  if (seconds == null) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

// Toolbar state — search + paging only (tenant is the sidebar switcher now).
const search = ref(props.q ?? '')
const page = ref(props.page ?? 1)

// Server-side: build the query from the current toolbar state and reload only
// the relevant props (the poll/back-button keep working; tenant is in session).
const visit = () => {
  const params = {}
  if (search.value) params.q = search.value
  if (page.value > 1) params.page = page.value
  router.get('/', params, {
    only: ['assets', 'q', 'page', 'per_page', 'total'],
    preserveState: true,
    preserveScroll: true,
  })
}

let searchTimer
const onSearch = (value) => {
  search.value = value
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    page.value = 1
    visit()
  }, 300)
}

const onPage = (value) => {
  page.value = value
  visit()
}

// While any asset is still being analyzed server-side, refresh the list every
// 2s so cards flip to "ready" without a manual reload. usePoll re-requests the
// current URL, so the active tenant/search/page are preserved automatically.
const anyProcessing = computed(() => props.assets.some((asset) => !asset.ready))
const { start, stop } = usePoll(2000, { only: ['assets'] }, { autoStart: false })
watch(anyProcessing, (busy) => (busy ? start() : stop()), { immediate: true })
</script>

<template>
  <div class="mb-4 flex flex-wrap items-center gap-2">
    <UInput
      :model-value="search"
      icon="i-lucide-search"
      placeholder="Search filename…"
      class="w-56"
      @update:model-value="onSearch"
    />
    <span class="text-sm text-muted">{{ total }} item{{ total === 1 ? '' : 's' }}</span>
  </div>

  <div v-if="assets.length === 0" class="py-16 text-center text-muted">
    Nothing
    <template v-if="search">matching “{{ search }}”</template>
    <template v-else>here yet</template>.
    <UButton to="/assets/new" variant="link" label="Upload one" />
  </div>

  <div v-else class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
    <UCard v-for="asset in assets" :key="asset.id" :ui="{ body: 'p-0 sm:p-0' }">
      <!-- thumb = 300x300 AVIF (imgproxy, on the fly). Images open the 1600px
           WebP rendition; videos open the player modal. For a video the thumb
           is its poster frame — see AssetSerializer. -->
      <component
        :is="asset.kind === 'video' ? 'button' : 'a'"
        :href="asset.kind === 'video' ? undefined : asset.large_url"
        :target="asset.kind === 'video' ? undefined : '_blank'"
        rel="noopener"
        class="relative block w-full"
        @click="asset.kind === 'video' && openPlayer(asset)"
      >
        <img
          :src="asset.thumb_url"
          :alt="asset.alt || asset.filename"
          loading="lazy"
          class="aspect-square w-full rounded-t-lg object-cover"
          :class="!asset.ready && 'opacity-50'"
        />
        <div class="absolute start-2 top-2 flex flex-wrap gap-1">
          <UBadge v-for="t in asset.tenants" :key="t.slug" color="neutral" variant="solid" :label="t.name" />
        </div>
        <UBadge
          v-if="!asset.ready"
          class="absolute end-2 top-2"
          color="info"
          variant="solid"
          icon="i-lucide-loader-circle"
          label="Analyzing…"
          :ui="{ leadingIcon: 'animate-spin' }"
        />
        <!-- video affordances: play overlay + duration -->
        <template v-if="asset.kind === 'video' && asset.ready">
          <span class="absolute inset-0 flex items-center justify-center">
            <UIcon name="i-lucide-circle-play" class="size-12 text-white/90 drop-shadow" />
          </span>
          <UBadge
            v-if="asset.duration"
            class="absolute end-2 bottom-2"
            color="neutral"
            variant="solid"
            :label="formatDuration(asset.duration)"
          />
        </template>
      </component>
      <div class="p-3">
        <div class="flex items-start justify-between gap-2">
          <p class="truncate text-sm font-medium" :title="asset.filename">{{ asset.filename }}</p>
          <UButton
            icon="i-lucide-pencil"
            color="neutral"
            variant="ghost"
            size="xs"
            aria-label="Edit image"
            @click="openEdit(asset)"
          />
        </div>
        <p v-if="asset.ready" class="text-xs text-muted">
          ID {{ asset.id }} · {{ asset.width }}×{{ asset.height }} · {{ formatBytes(asset.byte_size) }}
        </p>
        <p v-else class="flex items-center gap-1 text-xs text-info">
          ID {{ asset.id }} ·
          <UIcon name="i-lucide-loader-circle" class="size-3 animate-spin" />
          Processing…
        </p>
      </div>
    </UCard>
  </div>

  <div v-if="total > per_page" class="mt-6 flex justify-center">
    <UPagination :page="page" :items-per-page="per_page" :total="total" @update:page="onPage" />
  </div>

  <EditAssetModal v-model:open="editOpen" :asset="editingAsset" :tenants="tenants" />
  <VideoPlayerModal v-model:open="playerOpen" :asset="playingAsset" />
</template>
